import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: [
      { name: 'Help Desk Support', path: '/services#help-desk' },
      { name: 'Network Management', path: '/services#network' },
      { name: 'Cybersecurity', path: '/services#security' },
      { name: 'Cloud Services', path: '/services#cloud' },
      { name: 'Backup & Recovery', path: '/services#backup' },
      { name: 'Website Design', path: '/services#website' },
      { name: 'Structured Cabling', path: '/services#cabling' },
    ],
    company: [
      { name: 'About Us', path: '/about' },
      { name: 'Pricing', path: '/pricing' },
      { name: 'Contact', path: '/contact' },
    ],
    legal: [
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'Terms of Service', path: '/terms' },
      { name: 'Accessibility', path: '/accessibility' },
    ],
  };

  return (
    <footer className="bg-slate-950 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="/mlctech-logo.png" 
                alt="MLC TECH - IT, Security & Digital Solutions Company" 
                className="h-28 w-auto drop-shadow-[0_0_12px_rgba(96,165,250,0.25)]"
                style={{ filter: 'brightness(2.2) contrast(0.9)' }}
              />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Enterprise-grade IT solutions at prices small businesses can afford. 
              Proactive support, transparent pricing, real results.
            </p>
            <div className="space-y-3">
              <a 
                href="mailto:info@mlctech.org" 
                className="flex items-center gap-3 text-slate-400 hover:text-blue-400 transition-colors text-sm"
                data-testid="footer-email"
              >
                <Mail className="w-4 h-4" />
                info@mlctech.org
              </a>
              <a 
                href="tel:6026491314" 
                className="flex items-center gap-3 text-slate-400 hover:text-blue-400 transition-colors text-sm"
                data-testid="footer-phone"
              >
                <Phone className="w-4 h-4" />
                (602) 649-1314
              </a>
              <div className="flex items-center gap-3 text-slate-400 text-sm">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span>Serving businesses nationwide</span>
              </div>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300 mb-4">
              Services
            </h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-blue-400 transition-colors text-sm footer-link"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300 mb-4">
              Company
            </h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-blue-400 transition-colors text-sm footer-link"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Column */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300 mb-4">
              Legal
            </h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-blue-400 transition-colors text-sm footer-link"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {currentYear} MLC TECH. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <span className="text-slate-500 text-sm">
                Trusted by 100+ businesses
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
